import type { AppRouter } from '@app/api'
import { type LuachDay, SHKIA_LABEL, withSunset } from '@app/luach'
import {
  type CalendarSpelling,
  type PublicLanguage,
  type Said as SaidText,
  say,
} from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import { Fragment, type ReactNode, useState } from 'react'
import type { OrgListItem } from '../lib/org'
import { pdfUrl } from '../lib/pdf'
import { seedPersistentState } from '../lib/persist'
import { orgHref } from '../lib/public-router'
import { civilDay, civilWeekdayHe } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { Button, Notice } from '../ui'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { Said } from '../ui/Said'
import { Time } from './HomeTime'
import { Give, NamedHeading, Sections, Visit } from './home-sections'
import { Masthead } from './Masthead'
import './shul-home.css'
import {
  factsOf,
  namesOf,
  parshaBeneath,
  scheduleLabel,
  scheduleLink,
} from './shul-home'
import { TodayBand } from './TodayBand'

type Leading = inferRouterOutputs<AppRouter>['occasion']['leading']
type Lead = NonNullable<Leading['lead']>

/**
 * How often the page asks again what leads. The answer turns at candle
 * lighting and at the last time on a sheet, and what remains of a day shrinks
 * as it passes, so a page left open on a phone keeps up with the clock.
 */
const REREAD_MS = 60_000

/**
 * The most days that stand beside the headline. Two stacked are about the
 * lead's own height; a longer run takes the measure under a full-width title
 * as a row of days rather than a column taller than the lead.
 */
const DAYS_BESIDE = 2

/**
 * A congregation's front door: what is next at the shul, then giving, then how
 * to find it.
 *
 * The hero is ONE server read (`occasion.leading`) — which occurrence leads,
 * where the moment stands in it, and the lines of its own sheet. This page
 * composes sentences around that answer and decides nothing about the calendar:
 * the rule lives, and is tested, where the times are resolved.
 */
export function ShulHomePage({
  orgId,
  onLogin,
}: {
  orgId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.org.list.queryOptions())
  const listed = useQuery(trpc.org.directory.queryOptions())
  const shul = orgs.data?.find((s) => s.shortId === orgId)

  const leading = useQuery({
    ...trpc.occasion.leading.queryOptions({ spaceId: shul?.spaceId ?? '' }),
    enabled: !!shul,
    refetchInterval: REREAD_MS,
  })
  const masthead = useQuery(
    trpc.site.publicMasthead.queryOptions({ shortId: orgId }),
  )
  const [mastheadEl, setMastheadEl] = useState<HTMLElement | null>(null)

  // The page waits for its masthead too, so nothing jumps down under it.
  const loading = orgs.isLoading || masthead.isLoading
  if (loading || !shul) {
    return (
      <PublicShell name={null} orgId={null} onLogin={onLogin}>
        {loading ? <Notice>Loading…</Notice> : <PublicNotFound />}
      </PublicShell>
    )
  }

  const lead = leading.data?.lead ?? null
  return (
    <PublicShell
      name={shul.name}
      nameHe={shul.nameHe}
      language={shul.publicLanguage}
      orgId={shul.shortId}
      acceptsDonations={shul.acceptsDonations}
      siblings={listed.data}
      contact={shul}
      bleed
      masthead={masthead.data ? mastheadEl : null}
      onLogin={onLogin}
    >
      <div className="home">
        {/* A shul that drew a masthead opens on it, above what is next at the
            shul. Where nothing leads, the masthead is the page's heading, as
            on a site that leads with giving. */}
        {masthead.data ? (
          <Masthead
            ref={setMastheadEl}
            html={masthead.data.html}
            title={shul.name}
            heading={!(leading.data && lead)}
          />
        ) : null}
        {leading.data && lead ? (
          <Hero shul={shul} today={leading.data.today} lead={lead} />
        ) : (leading.isSuccess || leading.isError) && !masthead.data ? (
          <NamedHeading
            name={say(shul.publicLanguage.name, shul.name, shul.nameHe)}
          />
        ) : null}
        {leading.data?.comingUp ? (
          <ComingUp shul={shul} coming={leading.data.comingUp} />
        ) : null}
        {leading.data?.band ? (
          <TodayBand
            band={leading.data.band}
            place={shul.city}
            script={shul.publicLanguage.zmanim}
          />
        ) : null}
        <Sections orgId={shul.shortId} afterGiving={false} />
        {shul.acceptsDonations ? <Give orgId={shul.shortId} /> : null}
        <Sections orgId={shul.shortId} afterGiving />
        <Visit org={shul} />
      </div>
    </PublicShell>
  )
}

/**
 * Open the schedule on this occurrence. The link is the schedule's address;
 * which sheet it opens on is handed over as the schedule's own state.
 */
function openOn(o: {
  occasionKey: string
  startDate: string
  hebrewYear: number
}) {
  seedPersistentState('sched.occasion', o.occasionKey)
  seedPersistentState('sched.year', o.hebrewYear)
  seedPersistentState('sched.from', o.startDate)
  seedPersistentState('sched.picked', true)
}

/** "tomorrow", or the weekday — a yearly lead is never more than a week off. */
function whenIn(today: string, iso: string): string {
  const days = Math.round(
    (Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) /
      86_400_000,
  )
  return days === 1 ? 'tomorrow' : civilDay(iso, 'weekday')
}

const capitalized = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

type HeroCopy = {
  headline: ReactNode | null
  /** Hebrew set under the headline, or standing as the headline without one. */
  he: string | null
  beneath: SaidText | null
  /** The occasion, named in the date line where the headline is not its name. */
  dated?: SaidText
}

/**
 * The headline, the Hebrew set beneath it, and the line under that — each
 * present only where something is true of this occurrence, and the name in the
 * script the shul chose for occasion names. A name the shul reads in Hebrew
 * heads the page on its own, and what the English sentence said about it
 * moves to the line beneath.
 */
function heroCopy(
  lead: Lead,
  today: string,
  script: PublicLanguage['occasions'],
  spelling: CalendarSpelling,
): HeroCopy {
  const names = namesOf(lead, spelling)
  const name = say(script, names.en, names.he)
  /* A sentence about the occasion: the English name leads it, or the Hebrew
     name heads the page and what the sentence said follows beneath — the
     predicate alone, or `alone` where the predicate needs its subject. */
  const sentence = (rest: string, alone = rest): HeroCopy =>
    name.en
      ? { headline: `${name.en} ${rest}.`, he: name.he, beneath: null }
      : {
          headline: null,
          he: name.he,
          beneath: { en: `${capitalized(alone)}.`, he: null },
        }
  const named: HeroCopy = { headline: name.en, he: name.he, beneath: null }

  if (lead.state === 'erev' && lead.candleLighting) {
    return {
      headline: (
        <>
          Candle lighting tonight is <Time value={lead.candleLighting.time} />.
        </>
      ),
      he: null,
      beneath: name,
    }
  }
  // A day that lights no candles begins at nightfall: its evening's times are
  // what the visitor needs.
  if (lead.state === 'erev') return sentence('begins tonight')
  if (lead.state === 'during') {
    // The shul's own words where it has them, as written; its name otherwise.
    // A greeting says nothing of which day it greets, so the date line names
    // the occasion the way a luach heads a day.
    return lead.greetingHe
      ? { headline: null, he: lead.greetingHe, beneath: null, dated: name }
      : named
  }
  if (lead.cadence === 'weekly') {
    // Headed by its special name with the parsha beneath, where the English
    // name is a special one.
    const parsha = name.en ? parshaBeneath(lead, spelling) : null
    return { ...named, beneath: parsha ? { en: parsha, he: null } : null }
  }
  return lead.candleLighting
    ? sentence(`begins ${whenIn(today, lead.candleLighting.date)} evening`)
    : sentence(
        `is ${whenIn(today, lead.startDate)}`,
        whenIn(today, lead.startDate),
      )
}

/**
 * What follows the run, in one line: its first day and the last, by the names
 * the sheet heads them with.
 */
function AfterLine({
  after,
  script,
}: {
  after: NonNullable<Lead['after']>
  script: PublicLanguage['occasions']
}) {
  const firstName = say(script, after.first.labelEn, after.first.labelHe)
  const lastName = say(script, after.last.labelEn, after.last.labelHe)
  const first = <Said said={firstName} />
  const same = after.first.date === after.last.date
  /* Names the site reads in Hebrew alone are not set inside an English
     sentence: the line states the days as a list instead, each name with its
     weekday, so both ends of the range read alike. A line the site reads in
     Hebrew names its weekdays in Hebrew too: an English word inside it is
     reordered by the Hebrew around it. */
  if (!firstName.en || !lastName.en) {
    const on = (iso: string) => (
      <Said
        said={
          script === 'he'
            ? { en: null, he: civilWeekdayHe(iso) }
            : { en: civilDay(iso, 'weekday'), he: null }
        }
      />
    )
    return (
      <>
        {first} · {on(after.first.date)}
        {same ? null : (
          <>
            {' '}
            – <Said said={lastName} /> · {on(after.last.date)}
          </>
        )}
      </>
    )
  }
  const from = civilDay(after.first.date, 'weekday')
  if (same) {
    return (
      <span dir="ltr">
        {first} on {from}.
      </span>
    )
  }
  return (
    <span dir="ltr">
      {first} from {from}, and the rest to <Said said={lastName} />.
    </span>
  )
}

function Hero({
  shul,
  today,
  lead,
}: {
  shul: OrgListItem
  today: Leading['today']
  lead: Lead
}) {
  const lang = shul.publicLanguage
  const spelling = shul.calendarSpelling
  const copy = heroCopy(lead, today.date, lang.occasions, spelling)
  const facts = factsOf(lead, lang, spelling)
  const toSchedule = scheduleLabel(lead, lang.occasions, spelling)
  const beside = lead.days.length <= DAYS_BESIDE
  const dateSaid = say(
    lang.dates,
    civilDay(today.date, 'long'),
    today.hebrewDateHe,
  )
  /* The days run the way their lines read: where the shul names them in
     Hebrew the first day stands at the right, and each row puts its name at
     the right and its time at the left. */
  const days = lead.days.length ? (
    <div
      className={beside ? 'home__beside' : 'home__days'}
      dir={lang.lines === 'he' ? 'rtl' : 'ltr'}
    >
      {lead.days.map((d) => (
        <DayTimes key={d.dayOffset} day={d} language={lang} />
      ))}
    </div>
  ) : null

  /* The hero runs the way its headline reads: where the shul names its
     occasions in Hebrew the lead stands at the right and the days at the
     left. The days keep their own direction, which their lines decide, and
     an English sentence inside it keeps its own, so its closing period stays
     at its end. */
  return (
    <section
      className="home__hero"
      aria-labelledby="home-hero"
      dir={lang.occasions === 'he' ? 'rtl' : undefined}
    >
      <div className="home__measure home__hero-grid">
        <div
          className={
            days && beside ? 'home__lead' : 'home__lead home__lead--whole'
          }
        >
          <p className="home__today">
            {copy.dated ? (
              // One run, so the occasion joins the date as the page's other
              // lists join their parts rather than standing a gap apart.
              <span>
                <Said said={dateSaid} /> · <Said said={copy.dated} />
              </span>
            ) : (
              <Said said={dateSaid} />
            )}
          </p>
          <div className="home__titles">
            {copy.headline ? (
              <h1 id="home-hero" className="home__headline">
                <span dir="ltr">{copy.headline}</span>
              </h1>
            ) : null}
            {copy.he ? (
              copy.headline ? (
                <p className="home__headline-he home__he" lang="he" dir="rtl">
                  {copy.he}
                </p>
              ) : (
                <h1
                  id="home-hero"
                  className="home__headline home__headline--he"
                  lang="he"
                  dir="rtl"
                >
                  {copy.he}
                </h1>
              )
            ) : null}
            {copy.beneath ? (
              <p className="home__beneath">
                <Said said={copy.beneath} />
              </p>
            ) : null}
          </div>
          {facts.length ? (
            <p className="home__facts">
              {facts.map((f, i) => (
                <Fragment key={f.en ?? f.he}>
                  {i ? ' · ' : null}
                  <Said said={f} />
                </Fragment>
              ))}
            </p>
          ) : null}
          <div className="home__actions">
            <Button
              size="lg"
              render={
                <a
                  href={orgHref(shul.shortId, 'zmanim')}
                  onClick={() => openOn(lead)}
                />
              }
            >
              {toSchedule}
            </Button>
            <a
              className="home__textlink"
              href={pdfUrl({
                spaceId: shul.spaceId,
                occasionKey: lead.occasionKey,
                from: lead.startDate,
              })}
            >
              Print the sheet
            </a>
          </div>
        </div>
        {beside ? days : null}
      </div>
      <div className="home__measure">
        {beside ? null : days}
        {lead.after ? (
          <p className="home__after">
            <AfterLine after={lead.after} script={lang.occasions} />{' '}
            <a
              className="home__textlink"
              href={orgHref(shul.shortId, 'zmanim')}
              onClick={() => openOn(lead)}
            >
              See every day
            </a>
          </p>
        ) : null}
      </div>
    </section>
  )
}

/**
 * One day of the sheet, as the sheet lays it out: its lines in clock order with
 * shkia where it falls. The lines are the sheet's — label and time exactly as
 * resolved, nothing added.
 */
function DayTimes({
  day,
  language,
}: {
  day: LuachDay
  language: PublicLanguage
}) {
  return (
    <div className="home__day">
      <div className="home__day-head">
        <Said
          said={say(language.occasions, day.dayLabelEn, day.dayLabelHe)}
          className="home__day-name"
          heClassName="home__day-he"
        />
      </div>
      {withSunset(day).map((item) =>
        item.kind === 'anchor' ? (
          <div key="shkia" className="home__row">
            <span className="home__label">
              <Said
                said={say(language.lines, SHKIA_LABEL.en, SHKIA_LABEL.he)}
                heClassName="home__label-he"
              />
            </span>
            <span className="home__time">
              <Time value={item.time} />
            </span>
          </div>
        ) : (
          <div
            key={`${item.entry.lineId}-${item.entry.dayOffset}`}
            className="home__row"
          >
            <span className="home__label">
              <Said
                said={say(
                  language.lines,
                  item.entry.labelEn,
                  item.entry.labelHe,
                )}
                heClassName="home__label-he"
              />
            </span>
            <EntryTime entry={item.entry} />
          </div>
        ),
      )}
    </div>
  )
}

type Entry = LuachDay['entries'][number]

/** What stands in a line's time column: its time, each opinion's, or its words. */
function EntryTime({ entry }: { entry: Entry }) {
  if (entry.parts?.length) {
    return (
      <span className="home__time">
        {entry.parts.map((p, i) => (
          <span key={p.key}>
            {i ? ' / ' : ''}
            {p.time ? <Time value={p.time} /> : null}
          </span>
        ))}
      </span>
    )
  }
  if (entry.displayText) {
    return (
      <span className="home__note" lang="he" dir="rtl">
        {entry.displayText}
      </span>
    )
  }
  return entry.time ? (
    <span className="home__time">
      <Time value={entry.time} />
    </span>
  ) : null
}

/** One line for a yearly occasion close enough to plan for. */
function ComingUp({
  shul,
  coming,
}: {
  shul: OrgListItem
  coming: NonNullable<Leading['comingUp']>
}) {
  const lang = shul.publicLanguage
  const name = say(lang.occasions, coming.labelEn, coming.labelHe)
  const date = say(
    lang.dates,
    civilDay(coming.startDate, 'long'),
    coming.hebrewDateHe,
  )
  return (
    <div className="home__measure">
      <div className="home__coming">
        <p className="home__coming-line">
          {/* A sentence where both halves are English alone; otherwise the
              name and its date as a pair of facts, so no Hebrew is set
              inside an English sentence. */}
          {name.en && !name.he && date.en && !date.he ? (
            <>
              {name.en} is {date.en}.
            </>
          ) : (
            <>
              <Said said={name} /> · <Said said={date} />
            </>
          )}
        </p>
        <a
          className="home__textlink"
          href={orgHref(shul.shortId, 'zmanim')}
          onClick={() => openOn(coming)}
        >
          {scheduleLink(coming, lang.occasions)}
        </a>
      </div>
    </div>
  )
}
